import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog,
  faGlobe,
  faList,
  faCode,
  faClock,
  faChartLine,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import { SliderInput } from '../../shared/components/SliderInput'
import { saveLocalSetting, getLocalSettings } from '../../shared/utils/localStorage'
import { TIMEZONE_OPTIONS } from '../../shared/types/settings'

interface SettingsFormData {
  timezone: string
  listMax: number
  debugMode: boolean
  deviceTimeoutHours: number
  chartEditEnabled: boolean
}

interface CollapsibleSectionProps {
  id: string
  title: string
  icon: any
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({ id, title, icon, isOpen, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={icon} className="h-5 w-5 text-gray-600" />
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronRight}
          className="h-4 w-4 text-gray-400 transition-transform"
        />
      </button>

      {isOpen && (
        <div
          id={`section-${id}`}
          className="px-6 pb-6 pt-2 border-t border-gray-100"
        >
          {children}
        </div>
      )}
    </div>
  )
}

export function GeneralSettings() {
  const [formData, setFormData] = useState<SettingsFormData>(() => getLocalSettings())
  const [isLoading] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(['regional', 'display', 'developer', 'chart-edit', 'device-status'])
  )

  // Initialize form data from localStorage on mount
  useEffect(() => {
    const settings = getLocalSettings()
    setFormData(settings)
  }, [])

  const handleInputChange = (field: keyof SettingsFormData, value: string | number | boolean) => {
    // Update local state immediately
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Auto-save to localStorage immediately
    try {
      saveLocalSetting(field, value as any)
    } catch (error) {
      console.error(`Failed to save ${field} to localStorage:`, error)
      // Revert the change if save failed
      const settings = getLocalSettings()
      setFormData(settings)
    }
  }

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Regional Settings */}
      <CollapsibleSection
        id="regional"
        title="Regional Settings"
        icon={faGlobe}
        isOpen={openSections.has('regional')}
        onToggle={() => toggleSection('regional')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Zone
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              All timestamps will be displayed in this timezone (auto-saved)
            </p>
          </div>

          <div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">Current Time</h5>
              <div className="text-base font-mono text-blue-800 mb-1">
                {new Date().toLocaleString('en-US', {
                  timeZone: formData.timezone,
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZoneName: 'short',
                })}
              </div>
              <p className="text-xs text-blue-600">
                Preview of current time in selected timezone
              </p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Display Preferences */}
      <CollapsibleSection
        id="display"
        title="Display Preferences"
        icon={faList}
        isOpen={openSections.has('display')}
        onToggle={() => toggleSection('display')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Items Per Page
            </label>
            <input
              type="number"
              min="10"
              max="1000"
              step="10"
              value={formData.listMax}
              onChange={(e) => handleInputChange('listMax', Number(e.target.value))}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <p className="mt-2 text-xs text-gray-500">
              Maximum number of items to display in lists and tables (10-1000, auto-saved)
            </p>
          </div>

          <div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h5 className="text-sm font-semibold text-blue-900 mb-3">Recommended Values</h5>
              <div className="space-y-2 text-sm text-blue-800">
                <div>
                  <span className="font-semibold">50:</span> Good for mobile devices
                </div>
                <div>
                  <span className="font-semibold">100:</span> Default, balanced performance
                </div>
                <div>
                  <span className="font-semibold">200:</span> More data per page
                </div>
                <div>
                  <span className="font-semibold">500+:</span> High-performance systems only
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Enable Developer Mode */}
      <CollapsibleSection
        id="developer"
        title="Enable Developer Mode"
        icon={faCode}
        isOpen={openSections.has('developer')}
        onToggle={() => toggleSection('developer')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <div className="flex items-center justify-between py-2">
              <div className="flex-1">
                <p className="text-sm text-gray-700">
                  Show developer tools and debug features in the interface (auto-saved)
                </p>
              </div>
              <button
                type="button"
                className={`${
                  formData.debugMode ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-4`}
                role="switch"
                aria-checked={formData.debugMode}
                onClick={() => handleInputChange('debugMode', !formData.debugMode)}
              >
                <span
                  aria-hidden="true"
                  className={`${
                    formData.debugMode ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>

          <div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">About Developer Mode</h5>
              <p className="text-sm text-blue-800 leading-relaxed">
                Developer mode enables access to debug tools, device communication logs, raw message inspection, and
                testing features. When enabled, debug icons and developer menu items will appear in the interface.
              </p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Enable Chart Editing */}
      <CollapsibleSection
        id="chart-edit"
        title="Enable Chart Editing"
        icon={faChartLine}
        isOpen={openSections.has('chart-edit')}
        onToggle={() => toggleSection('chart-edit')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <div className="flex items-center justify-between py-2">
              <div className="flex-1">
                <p className="text-sm text-gray-700">
                  Show the Edit button on charts to hide or restore sensor data (auto-saved)
                </p>
              </div>
              <button
                type="button"
                className={`${
                  formData.chartEditEnabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-4`}
                role="switch"
                aria-checked={formData.chartEditEnabled}
                onClick={() => handleInputChange('chartEditEnabled', !formData.chartEditEnabled)}
              >
                <span
                  aria-hidden="true"
                  className={`${
                    formData.chartEditEnabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>

          <div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">About Chart Editing</h5>
              <p className="text-sm text-blue-800 leading-relaxed">
                When enabled, an Edit button appears on charts allowing you to hide or restore sensor data within
                selected time ranges. This is useful for removing erroneous readings or restoring previously hidden
                data.
              </p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Device Status Detection */}
      <CollapsibleSection
        id="device-status"
        title="Device Status Detection"
        icon={faClock}
        isOpen={openSections.has('device-status')}
        onToggle={() => toggleSection('device-status')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div>
            <SliderInput
              value={formData.deviceTimeoutHours}
              onChange={(value) => handleInputChange('deviceTimeoutHours', value)}
              min={1}
              max={48}
              step={1}
              presets={[1, 6, 12, 24, 48]}
              label="Device Offline Timeout"
              description="How long to wait before marking a device as offline"
              unit="h"
              formatValue={(value) => (value === 1 ? '1 hour' : `${value} hours`)}
            />
          </div>

          <div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
              <h5 className="text-sm font-semibold text-amber-900 mb-3">How This Works</h5>
              <div className="space-y-2.5 text-sm text-amber-800">
                <div className="flex items-start space-x-2.5">
                  <div className="w-3 h-3 bg-green-500 rounded-full mt-0.5 flex-shrink-0"></div>
                  <span>
                    Device is <strong>online</strong> when sensor data received recently
                  </span>
                </div>
                <div className="flex items-start space-x-2.5">
                  <div className="w-3 h-3 bg-red-500 rounded-full mt-0.5 flex-shrink-0"></div>
                  <span>
                    Device is <strong>offline</strong> when no data for{' '}
                    {formData.deviceTimeoutHours === 1 ? '1 hour' : `${formData.deviceTimeoutHours} hours`}
                  </span>
                </div>
                <div className="flex items-start space-x-2.5">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mt-0.5 flex-shrink-0"></div>
                  <span>Amber pins indicate location-only data</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Settings Help */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-6">
        <div className="flex">
          <FontAwesomeIcon icon={faCog} className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Settings Help</h3>
            <div className="text-sm text-blue-800 space-y-1.5">
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  <strong>Time Zone:</strong> Affects all date/time displays throughout the application
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  <strong>Items Per Page:</strong> Controls how many records are shown in device lists, charts, and data tables
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  <strong>Developer Mode:</strong> Controls whether debug tools and developer features are visible in the interface
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  <strong>Chart Editing:</strong> When enabled, shows an Edit button on charts to hide or restore sensor data within time ranges
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  <strong>Device Timeout:</strong> Controls how long to wait before marking devices as offline based on their last sensor reading
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  All changes are saved automatically when you make them
                </span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Settings are synchronized across all your devices and sessions
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
